import React, { useCallback } from "react";
import { useTheme } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DSBox from "../../../system-design/DSBox";
import DSIconButton from "../../../system-design/DSIconButton";
import DSTypography from "../../../system-design/DSTypography";
import DSTooltip from "../../../system-design/DSTooltip";
import CollectionAssetFileInputEmpty from "./CollectionAssetFileInputEmpty";
import CollectionAssetFileInputLoading from "./CollectionAssetFileInputLoading";
import CollectionAssetFileInputUploaded from "./CollectionAssetFileInputUploaded";

type CollectionAssetFileInputProps = {
  label: string;
  fileSpecsText: string;
  acceptedFormats: string;
  helpTooltipContent?: React.ReactNode;
  secureUrl: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  fileName: string;
  isLoading: boolean;
  required?: boolean;
  disabled?: boolean;
};

const CollectionAssetFileInput = ({
  label,
  fileSpecsText,
  acceptedFormats,
  helpTooltipContent,
  secureUrl,
  onFileChange,
  onRemoveFile,
  fileName,
  isLoading,
  required = false,
  disabled = false,
}: CollectionAssetFileInputProps) => {
  const { spacing, palette } = useTheme();

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFileChange(event);
    },
    [onFileChange],
  );

  const handleRemoveFile = useCallback(() => {
    onRemoveFile();
  }, [onRemoveFile]);

  const renderFileInputState = () => {
    if (isLoading) {
      return <CollectionAssetFileInputLoading />;
    }

    if (secureUrl) {
      return (
        <CollectionAssetFileInputUploaded
          secureUrl={secureUrl}
          fileName={fileName}
          downloadFileName={label.toLowerCase().replace(/\s+/g, "-")}
          onRemoveFile={handleRemoveFile}
          disabled={disabled}
        />
      );
    }

    return (
      <CollectionAssetFileInputEmpty
        fileSpecsText={fileSpecsText}
        acceptedFormats={acceptedFormats}
        onFileChange={handleFileChange}
        disabled={disabled}
      />
    );
  };

  return (
    <DSBox
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: spacing(1),
        width: "100%",
      }}
    >
      <DSBox
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: spacing(0.5),
        }}
      >
        <DSTypography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: palette.text.secondary,
          }}
        >
          {required && "*"}
          {label}
        </DSTypography>
        {helpTooltipContent && (
          <DSTooltip title={helpTooltipContent}>
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: "color 0.3s ease",
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        )}
      </DSBox>
      <DSBox>{renderFileInputState()}</DSBox>
    </DSBox>
  );
};

export default CollectionAssetFileInput;
